import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { formatPhoneNumber, isValidPhoneNumber } from '@/utils/phoneFormat';
import { useToast } from '@/hooks/use-toast';
import { z } from 'zod';

const contactSchema = z.object({
  formType: z.string().default('Contact'),
  firstName: z.string().trim().min(1, 'First name is required').max(50, 'First name must be less than 50 characters'),
  lastName: z.string().trim().min(1, 'Last name is required').max(50, 'Last name must be less than 50 characters'),
  email: z.string().trim().email('Please enter a valid email address').max(255, 'Email must be less than 255 characters'),
  phoneNumber: z.string().trim().max(20, 'Phone number must be less than 20 characters'),
  message: z.string().trim().min(1, 'Message is required').max(2000, 'Message must be less than 2000 characters')
});

const ContactForm = ({ noBackground = false }: { noBackground?: boolean }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    formType: 'Contact',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate form data
      const validatedData = contactSchema.parse(formData);
      
      const response = await fetch('https://hooks.zapier.com/hooks/catch/21776725/u1pdpuq/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'no-cors',
        body: JSON.stringify({
          ...validatedData,
          fullName: `${validatedData.firstName} ${validatedData.lastName}`,
          timestamp: new Date().toISOString(),
          source: window.location.origin
        }),
      });

      toast({
        title: "Message Sent",
        description: "Thank you for your message. We'll get back to you soon!",
      });

      // Reset form after successful submission
      setFormData({
        formType: 'Contact',
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        message: ''
      });

    } catch (error) {
      if (error instanceof z.ZodError) {
        // Display validation errors
        const firstError = error.errors[0];
        toast({
          title: "Validation Error",
          description: firstError?.message || "Please check your input and try again.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to send message. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'phoneNumber') {
      const formattedPhone = formatPhoneNumber(value);
      setFormData({
        ...formData,
        [name]: formattedPhone
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  return (
    <div className="w-full">
      <div className="mb-4 md:mb-6 flex items-center gap-3">
        <img 
          src="/lovable-uploads/a53f5ba2-203a-4732-899c-ff2777cb7d2c.png" 
          alt="Freeman & Crown Law Logo" 
          className="w-7 h-7"
        />
        <h2 className={`font-rockwell text-lg md:text-xl font-semibold ${noBackground ? 'text-foreground' : 'text-white'}`}>
          Let's Talk
        </h2>
      </div>
      <form onSubmit={handleSubmit} className="space-y-2 md:space-y-3">
        <div>
          <Input
            id="firstName"
            name="firstName"
            type="text"
            placeholder="First Name"
            value={formData.firstName}
            onChange={handleChange}
            required
            className={`w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg text-xs md:text-sm transition-all ${noBackground ? 'border-border bg-background text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-0' : 'border-white/30 bg-white/20 backdrop-blur-sm text-white placeholder:text-white/70 focus:border-white focus:ring-0 focus:bg-white/30'}`}
          />
        </div>

        <div>
          <Input
            id="lastName"
            name="lastName"
            type="text"
            placeholder="Last Name"
            value={formData.lastName}
            onChange={handleChange}
            required
            className={`w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg text-xs md:text-sm transition-all ${noBackground ? 'border-border bg-background text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-0' : 'border-white/30 bg-white/20 backdrop-blur-sm text-white placeholder:text-white/70 focus:border-white focus:ring-0 focus:bg-white/30'}`}
          />
        </div>

        <div>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
            required
            className={`w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg text-xs md:text-sm transition-all ${noBackground ? 'border-border bg-background text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-0' : 'border-white/30 bg-white/20 backdrop-blur-sm text-white placeholder:text-white/70 focus:border-white focus:ring-0 focus:bg-white/30'}`}
          />
        </div>

        <div>
          <Input
            id="phoneNumber"
            name="phoneNumber"
            type="tel"
            placeholder="Phone"
            value={formData.phoneNumber}
            onChange={handleChange}
            className={`w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg text-xs md:text-sm transition-all ${noBackground ? 'border-border bg-background text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-0' : 'border-white/30 bg-white/20 backdrop-blur-sm text-white placeholder:text-white/70 focus:border-white focus:ring-0 focus:bg-white/30'}`}
          />
        </div>

        <div>
          <Textarea
            id="message"
            name="message"
            placeholder="Type your message here..."
            value={formData.message}
            onChange={handleChange}
            required
            rows={3}
            className={`w-full px-3 md:px-4 py-2 md:py-3 border rounded-lg text-xs md:text-sm resize-none transition-all ${noBackground ? 'border-border bg-background text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-0' : 'border-white/30 bg-white/20 backdrop-blur-sm text-white placeholder:text-white/70 focus:border-white focus:ring-0 focus:bg-white/30'}`}
          />
        </div>

        <Button 
          type="submit" 
          disabled={isSubmitting}
          className={`w-full rounded-lg py-2 md:py-3 text-xs md:text-sm font-semibold transition-all shadow-lg ${noBackground ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-white text-primary hover:bg-white/90'}`}
        >
          {isSubmitting ? 'Sending...' : 'Submit'}
        </Button>
      </form>
    </div>
  );
};

export default ContactForm;